/**
 * The browser side of PandaDoc: calls the platform's pandadoc function with the signed-in
 * founder's token. In the demo (no Supabase) a stand-in records documents in memory.
 */
import type { Db } from "../data/db.ts";
import type { MemoryDb } from "../data/memory-db.ts";
import type { LeadDocument, SendInput } from "./pandadoc.ts";

export interface DocsApi {
  readonly demo: boolean;
  templates(): Promise<{ id: string; name: string }[]>;
  send(input: SendInput): Promise<LeadDocument>;
  sendDraft(id: string): Promise<LeadDocument>;
  refresh(id: string): Promise<LeadDocument>;
  download(id: string): Promise<Blob>;
}

const ENDPOINT = "/api/pandadoc";

export function docsApi(db: Db): DocsApi {
  if (db.kind === "memory") return demoDocs(db as unknown as MemoryDb);
  const call = async (body: unknown): Promise<Response> => {
    const token = await db.accessToken();
    if (!token) throw new Error("Sign in again");
    const res = await fetch(ENDPOINT, { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (!res.ok) {
      const msg = await res.json().then((j: { error?: string }) => j.error, () => null);
      throw new Error(msg ?? `PandaDoc request failed (${res.status})`);
    }
    return res;
  };
  const doc = async (body: unknown) => ((await (await call(body)).json()) as { document: LeadDocument }).document;
  return {
    demo: false,
    templates: async () => ((await (await call({ action: "templates" })).json()) as { templates: { id: string; name: string }[] }).templates,
    send: (input) => doc({ action: "send", input }),
    sendDraft: (id) => doc({ action: "send-draft", id }),
    refresh: (id) => doc({ action: "refresh", id }),
    download: async (id) => (await call({ action: "download", id })).blob(),
  };
}

/** Demo only: documents are "sent" at once and "signed" the next time their status is checked. */
function demoDocs(db: MemoryDb): DocsApi {
  return {
    demo: true,
    templates: async () => [{ id: "demo-engagement", name: "Engagement letter (demo)" }, { id: "demo-proposal", name: "Proposal (demo)" }],
    send: async (input) => {
      const now = new Date().toISOString();
      const d = await db.demoSaveDocument({
        lead_id: input.lead_id, pandadoc_id: `demo-${Math.random().toString(16).slice(2, 10)}`, kind: input.kind,
        name: `${input.kind === "proposal" ? "Proposal" : "Engagement letter"}: ${input.company}`, template_name: input.template_name ?? null,
        recipient_email: input.recipient.email, status: "sent", sent_at: now, viewed_at: null, completed_at: null, last_checked_at: now, created_by: (await db.currentUser())?.email ?? null,
      });
      const lead = await db.getLead(input.lead_id);
      if (lead && ["replied", "call_booked", "call_done"].includes(lead.status)) await db.updateLead(lead.id, { status: "proposal_sent", last_contact_at: now });
      await db.addLeadNote({ lead_id: input.lead_id, kind: "system", body: `${input.kind === "proposal" ? "Proposal" : "Engagement letter"} sent through PandaDoc to ${input.recipient.email}.` });
      return d;
    },
    sendDraft: async (id) => db.demoUpdateDocument(id, { status: "sent", sent_at: new Date().toISOString() }),
    refresh: async (id) => {
      const now = new Date().toISOString();
      const d = await db.demoUpdateDocument(id, { status: "completed", completed_at: now, last_checked_at: now });
      await db.addLeadNote({ lead_id: d.lead_id, kind: "system", body: `${d.kind === "proposal" ? "Proposal" : "Engagement letter"} signed in PandaDoc. Next: start the audit.` });
      const lead = await db.getLead(d.lead_id);
      if (lead?.status === "proposal_sent") await db.updateLead(lead.id, { next_step: "Signed: start the audit", next_step_due: now.slice(0, 10) });
      return d;
    },
    download: async () => new Blob(["Demo: the signed PDF comes from PandaDoc."], { type: "text/plain" }),
  };
}
